import { AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { useCallback, useRef, useState } from "react";

// import { HTTPClient } from "@/ui/api/httpClient";
import { HTTPClient } from "../../../api/httpClient";
// import { AddressSuggestion } from "@/components/GoogleAddressAutocomplete/types";
import { AddressSuggestion } from "./types";
// import { COOKIE_KEYS } from "@/ui/constants/cookie";
import { COOKIE_KEYS } from "../../constants/cookie";
// import { STATIC_LOCATION_TYPE } from "@/ui/constants/staticMaps";
import { STATIC_LOCATION_TYPE } from "../../constants/staticMaps";
// import { parseCustomAddress } from "@/ui/helpers/string";
import { parseCustomAddress } from "../../../helpers/string";
import {
  APIContext,
  Address,
  CreateSessionResponse,
  SetAddressBody,
  SetServiceAddressResponse,
} from "../../../api/Session";
// import { APIContext } from "../../../api/Session";
// import { toTitleCase } from "@/ui/utils";
import { toTitleCase } from "../../../utils";

const windstreamAPIClient = new HTTPClient({
  baseURL: `${process.env.REACT_APP_API_URL}/v1`,
  authorized: true,
});

const startSession = (context?: APIContext): Promise<CreateSessionResponse> => {
  return windstreamAPIClient.post<CreateSessionResponse>(
    `auth/session/?context=${context}`,
    {},
  );
};

export const searchAddressService = (
  serviceAddress: SetAddressBody,
  options: AxiosRequestConfig,
) => {
  return windstreamAPIClient.post<SetServiceAddressResponse>(
    process.env.REACT_APP_USE_OLD_MIDDLEWARE === "true"
      ? "user-info/set-service-address"
      : "address/search",
    serviceAddress,
    options,
  );
};

export function useUqualSuggestions() {
  const axiosAbortController = useRef<AbortController>();
  const uqualCallCount = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AddressSuggestion[]>();
  const [error, setError] = useState<any>();

  const getUqualAddressSuggestions = useCallback(
    async (address: SetAddressBody) => {
      axiosAbortController.current?.abort();
      const abortController = new AbortController();
      axiosAbortController.current = abortController;

      if (!Cookies.get(COOKIE_KEYS.WINDSTREAM_CART_TOKEN)) {
        console.log("WARNING - SHOULD ONLY BE CALLED IN RESIDENTIAL");
        const { token } = await startSession(APIContext.Residential);
        if (token) Cookies.set(windstreamAPIClient.cookieKey, token);
      }

      try {
        const setAddressResponse = await searchAddressService(address, {
          signal: abortController.signal,
        });
        return setAddressResponse.addressCandidates;
      } catch (error) {
        throw error;
      }
    },
    [],
  );

  const setUqualSearchValue = useCallback(
    (value: string, shouldFetchData: boolean) => {
      if (!value) {
        setData([]);
        axiosAbortController.current?.abort();
        return;
      }

      if (shouldFetchData) {
        setIsLoading(true);
        setError(null);
        const setAddressValue = parseCustomAddress(value);

        uqualCallCount.current++;
        getUqualAddressSuggestions(setAddressValue)
          .then(result => mapUqualSuggestions(result))
          .then(suggestions => setData(suggestions))
          .catch((err: any) => {
            console.error(err);
            setData([]);
            setError(err);
          })
          .finally(() => {
            if (--uqualCallCount.current === 0) setIsLoading(false);
          });
      }
    },
    [getUqualAddressSuggestions],
  );

  const clearSuggestions = useCallback(() => {
    setData([]);
  }, []);

  return {
    isLoading,
    data: data,
    error,
    setUqualSearchValue,
    clearSuggestions,
  };
}

const createAddressSuggestionFromAddress = (
  address: Address,
  unit?: string,
): AddressSuggestion => {
  const mainText = [address.addressLine1, address.addressLine2, unit]
    .filter(Boolean)
    .join(" ");
  return {
    mainText: `${toTitleCase(mainText)}`,
    address,
    locationType: STATIC_LOCATION_TYPE,
  };
};

function mapUqualSuggestions(uqualSuggestions: Address[]): AddressSuggestion[] {
  return uqualSuggestions.reduce<AddressSuggestion[]>((acc, cur) => {
    if (cur.units?.length) {
      const addressSuggestionsWithUnits: AddressSuggestion[] = cur.units.map(
        unit => createAddressSuggestionFromAddress(cur, unit.addressLine2),
      );
      return [...acc, ...addressSuggestionsWithUnits];
    } else return [...acc, createAddressSuggestionFromAddress(cur)];
  }, []);
}
